import { UNIT_CONVERSIONS } from '../common/unitConversions'
import { UnitCategoryId } from '../common/enums'

type Operation = { op: '*' | '/' | '+' | '-'; value: number }

/**
 * Parse a unit conversion formula string into operations.
 *
 * Formula format:
 * - `@` = input value placeholder
 * - `[n]` = constant value
 * - `*`, `/`, `+`, `-` = operations (applied to the accumulated result)
 *
 * Examples:
 * - `@` = identity (no conversion)
 * - `@[1000]*` = value * 1000
 * - `@[1.8]*[32]+` = (value * 1.8) + 32 (C to F)
 * - `@[1000]/` = value / 1000
 */
function parseFormulaOperations(formula: string): Operation[] {
  if (!formula || formula === '@') {
    return []
  }

  const operations: Operation[] = []
  const regex = /\[([^\]]+)\]([*/+-])/g
  let match

  while ((match = regex.exec(formula)) !== null) {
    const constantValue = parseFloat(match[1])
    const operator = match[2] as '*' | '/' | '+' | '-'
    operations.push({ op: operator, value: constantValue })
  }

  return operations
}

/**
 * Apply operations to a value (forward conversion: base -> target)
 */
function applyOperations(inputValue: number, operations: Operation[]): number {
  let result = inputValue
  for (const { op, value } of operations) {
    switch (op) {
      case '*':
        result = result * value
        break
      case '/':
        result = result / value
        break
      case '+':
        result = result + value
        break
      case '-':
        result = result - value
        break
    }
  }
  return result
}

/**
 * Apply inverse operations to a value (reverse conversion: target -> base)
 * Operations are applied in reverse order with inverse operators.
 */
function applyInverseOperations(inputValue: number, operations: Operation[]): number {
  let result = inputValue
  // Apply in reverse order with inverse operations
  for (let i = operations.length - 1; i >= 0; i--) {
    const { op, value } = operations[i]
    switch (op) {
      case '*':
        result = result / value
        break
      case '/':
        result = result * value
        break
      case '+':
        result = result - value
        break
      case '-':
        result = result + value
        break
    }
  }
  return result
}

/**
 * Parse a unit conversion formula string and return a function that applies it.
 */
export function parseConversionFormula(formula: string): (value: number) => number {
  const operations = parseFormulaOperations(formula)
  return (inputValue: number) => applyOperations(inputValue, operations)
}

/**
 * Convert a value from base units to target units for a given unit category.
 *
 * @param value - The value in base units
 * @param unitCategoryId - The unit category ID
 * @param targetUnitId - The target unit ID to convert to
 * @returns The converted value
 */
export function convertValue(
  value: number,
  unitCategoryId: number,
  targetUnitId: number
): number {
  const categoryConversions = UNIT_CONVERSIONS[unitCategoryId as UnitCategoryId]
  if (!categoryConversions) {
    return value
  }

  const formula = categoryConversions.formulas[targetUnitId]
  if (!formula) {
    return value
  }

  const operations = parseFormulaOperations(formula)
  return applyOperations(value, operations)
}

/**
 * Convert a value between any two units within the same category.
 *
 * @param value - The value in source units
 * @param unitCategoryId - The unit category ID
 * @param sourceUnitId - The source unit ID the value is currently in
 * @param targetUnitId - The target unit ID to convert to
 * @returns The converted value
 */
export function convertBetweenUnits(
  value: number,
  unitCategoryId: number,
  sourceUnitId: number,
  targetUnitId: number
): number {
  // Same unit, no conversion needed
  if (sourceUnitId === targetUnitId) {
    return value
  }

  const categoryConversions = UNIT_CONVERSIONS[unitCategoryId as UnitCategoryId]
  if (!categoryConversions) {
    return value
  }

  const sourceFormula = categoryConversions.formulas[sourceUnitId]
  const targetFormula = categoryConversions.formulas[targetUnitId]

  if (!sourceFormula || !targetFormula) {
    return value
  }

  // Step 1: Convert from source unit to base unit (inverse of source formula)
  const sourceOps = parseFormulaOperations(sourceFormula)
  const baseValue = applyInverseOperations(value, sourceOps)

  // Step 2: Convert from base unit to target unit
  const targetOps = parseFormulaOperations(targetFormula)
  return applyOperations(baseValue, targetOps)
}

/**
 * Get the abbreviation for a unit within a category.
 *
 * @param unitCategoryId - The unit category ID
 * @param unitId - The unit ID
 * @returns The unit abbreviation or empty string if not found
 */
export function getUnitAbbreviation(
  unitCategoryId: number,
  unitId: number
): string {
  const categoryConversions = UNIT_CONVERSIONS[unitCategoryId as UnitCategoryId]
  if (!categoryConversions) {
    return ''
  }

  return categoryConversions.abbreviations[unitId] || ''
}

/**
 * Get available units for a unit category.
 *
 * @param unitCategoryId - The unit category ID
 * @returns Array of { unitId, name, abbreviation } for available units
 */
export function getAvailableUnits(
  unitCategoryId: number
): Array<{ unitId: number; name: string; abbreviation: string }> {
  const categoryConversions = UNIT_CONVERSIONS[unitCategoryId as UnitCategoryId]
  if (!categoryConversions) {
    return []
  }

  return Object.entries(categoryConversions.available).map(([unitIdStr, name]) => {
    const unitId = parseInt(unitIdStr, 10)
    return {
      unitId,
      name,
      abbreviation: categoryConversions.abbreviations[unitId] || name,
    }
  })
}

/**
 * Get the base unit ID for a unit category.
 *
 * @param unitCategoryId - The unit category ID
 * @returns The base unit ID or undefined if not found
 */
export function getBaseUnitId(unitCategoryId: number): number | undefined {
  const categoryConversions = UNIT_CONVERSIONS[unitCategoryId as UnitCategoryId]
  return categoryConversions?.metadata?.baseUnitId
}

/**
 * Get metadata for a unit category.
 *
 * @param unitCategoryId - The unit category ID
 * @returns Category metadata or undefined if not found
 */
export function getUnitCategoryMetadata(
  unitCategoryId: number
): { shortName: string; longName: string; shortUnits: string; longUnits: string } | undefined {
  const categoryConversions = UNIT_CONVERSIONS[unitCategoryId as UnitCategoryId]
  if (!categoryConversions?.metadata) {
    return undefined
  }

  return {
    shortName: categoryConversions.metadata.shortName,
    longName: categoryConversions.metadata.longName,
    shortUnits: categoryConversions.metadata.shortUnits,
    longUnits: categoryConversions.metadata.longUnits,
  }
}
